/**
 * **Hide — the stealth action's rules core.** Pure rules (no I/O, no dice): the cover
 * precondition the gameplay Hide is gated by, the grounded step the Hide resolution emits,
 * and the shared clear-Hidden helper the reveal triggers fire. The room
 * (`backend/.../encounterRoom.ts`) calls these; the geometry it leans on
 * (`terrainVisibility` → `los.ts`'s `sight`/`visibility`) is reused as-is, never rebuilt.
 *
 * **The two-tier split.** {@link checkHideCover} is the rules-following
 * gameplay precondition: a unit may Hide only if it has cover from / is unseen by **every**
 * hostile — if **any** hostile has a fully clear line (`visible && cover === "none"`) the
 * unit is in the open and Hide is hard-rejected. A GM-forced Hide (`gm_force`) skips this
 * check entirely (it stamps Hidden unconditionally) — but that is a SEPARATE named intent in
 * the room, NEVER a `force` flag here; this module only supplies the gameplay gate + the
 * shared step builder both tiers apply.
 *
 * **Hidden is GLOBAL** (RAW: hidden-from-all, not per-attacker) — a single `HIDDEN_STATUS`
 * on the combatant, applied/cleared as a reified grounded step so every reveal is invertible
 * and replay-safe (the crown-jewel invariant).
 */

import {
  type EncounterState,
  type Combatant,
  type ResolvedStep,
  worldFor,
  sideOf,
  isActor,
  hasStatus,
  terrainVisibility,
  HIDDEN_STATUS,
  INVISIBLE_STATUS,
  PROWLING_STATUS,
} from "@lancer-vtt/domain";
import { type Result, ok, err } from "@lancer-vtt/shared";
import { footprintCells } from "./effects/spatialAnswerer";

/** The status `ms_tracking_bug` plants; its carrier can't Hide. */
export const TRACKING_BUG_STATUS = "Tracking Bug";

/** The status the Scout's Marker Rifle plants beside a Lock On; its carrier can't Hide while it holds the Lock On. */
export const MARKER_RIFLE_MARK_STATUS = "Marker Rifle Mark";

/**
 * The **cover auto-check** the gameplay Hide is gated by. RAW: you can Hide
 * only "if you have cover or are obscured" from those who might see you. We read that off
 * the live map via {@link terrainVisibility} (the same `los.ts` geometry the `line_of_sight`
 * predicate answers with during resolve): for every **hostile** with a placed footprint, the
 * sightline *to the hider* must grant some cover, or be blocked. If **any** hostile has a
 * fully clear line — `visible && cover === "none"` — the hider is in the open and the Hide is
 * rejected.
 *
 * Hostility is the activation **side** split (`sideOf`: mech/pilot ⇒ pc, else npc), the one
 * team model the engine has — mirroring `engagementRecomputer`. An unplaced hider can't be
 * measured, so it's rejected (you can't Hide off the map). With no placed hostiles the check
 * passes vacuously (no one is watching).
 *
 * **Only actors can watch**: `sideOf` folds every non-mech/pilot kind — including
 * `object`/`deployable` — to `"npc"`, so a wall would otherwise look like a hostile "watcher"
 * of a pc-side hider. RAW objects have no eyes (no turns, no E-Defense, no senses); a placed
 * object/deployable is skipped by the SAME `isActor` predicate that already excludes it from
 * activation, never counted as a candidate hostile, whether or not it's Destroyed.
 *
 * **Invisible needs no cover** (RAW Core p.77: "an Invisible character can always Hide, even
 * without cover") — checked right after the on-map/placed guards
 * (an unplaced hider is still rejected; Invisible doesn't excuse being off the map), before the
 * per-hostile cover scan even runs.
 *
 * **This same short-circuit is also what produces the Invisible→Hidden reveal** (RAW Core p.69:
 * "If you're hiding while INVISIBLE, you lose HIDDEN when you cease to be INVISIBLE unless you
 * are in cover"). There is no dedicated reveal trigger for
 * this — once Invisible is gone from `hider.statuses`, this exact function stops short-circuiting
 * and falls through to the real per-hostile scan below, exactly as if the unit had never been
 * Invisible. The RAW behavior is therefore an emergent property of *when* this predicate gets
 * re-run relative to Invisible's removal: `turn_ended`'s `status_expiry` rule then its
 * `reveal_exposed` rule (`referee/refereeCommand.ts`'s `TURN_ENDED_RULES`) remove Invisible first,
 * then re-run this cover check against that post-removal state — see
 * {@link revealExposedAtTurnEndSteps}'s doc comment for the full chain and the accepted gaps (GM
 * manual status removal, ally-cast Invisible) that are deliberately NOT covered by this mechanism
 * today.
 *
 * Returns the shared {@link Result} verdict the room surfaces (a rejection is a logged no-op
 * before the Hide resolution). Pure over `state`: reads positions/terrain, never mutates.
 */
export function checkHideCover(state: EncounterState, actor: string): Result<void> {
  const hider = state.combatants[actor];
  if (!hider) return err(`hide: ${actor} is not on the map`);
  if (!hider.pos) return err(`hide: ${hider.name} is not placed on the map (can't measure cover)`);
  // A tracking bug's carrier "can't Hide" (`ms_tracking_bug`), Invisible or not; the reveal sweeps
  // read this same check, so a bugged unit that was already Hidden is revealed too.
  if (hasStatus(hider.statuses, TRACKING_BUG_STATUS)) return err(`hide: ${hider.name} carries a tracking bug and can't Hide`);
  if (hasStatus(hider.statuses, MARKER_RIFLE_MARK_STATUS) && hasStatus(hider.statuses, "Lock On")) return err(`hide: ${hider.name} is marked by a Marker Rifle and holds a Lock On, so can't Hide`);
  if (hasStatus(hider.statuses, INVISIBLE_STATUS)) return ok(undefined);

  const world = worldFor(state);
  const hiderFp = footprintCells(world, hider);
  const mySide = sideOf(hider);

  // Sort ids so the "first exposing hostile" reported is deterministic (replay/test-stable),
  // never `combatants` insertion order.
  for (const id of Object.keys(state.combatants).sort()) {
    const other = state.combatants[id];
    if (!other || other.id === actor) continue;
    if (!other.pos) continue; // unplaced — not watching from anywhere.
    // an object/deployable has no eyes — it never watches. A `drone` is ALSO skipped here
    // even though it IS a character — drones carry no sensor/observer text (a non-actor,
    // most can't act at all), so a drone stays a non-observer for Hide purposes. A
    // deliberate scope call, not a gap: a Scout-type sensor drone that DOES watch would be
    // item-specific content.
    if (!isActor(other)) continue;
    if (sideOf(other) === mySide) continue; // only a hostile can expose the hider.
    const view = terrainVisibility({
      space: world,
      terrain: state.terrain,
      from: footprintCells(world, other),
      to: hiderFp,
      edges: state.edges,
      combatants: state.combatants,
      areaEffects: state.areaEffects,
    });
    if (view.visible && view.cover === "none") {
      return err(
        `hide: ${hider.name} is in the open — ${other.name} has a clear line of sight with no cover; ` +
          `Hide needs cover or concealment from every hostile`,
      );
    }
  }
  return ok(undefined);
}

/**
 * The grounded steps a Hide resolution applies: land {@link HIDDEN_STATUS} on the
 * actor, `until_removed` (Hidden persists until a reveal trigger clears it). Idempotent: a unit
 * already Hidden yields **no** step (so the room broadcasts nothing) — the common path stays a
 * single clean `status_add`. Both the gameplay `request_hide` and a GM-forced Hide apply
 * these same steps; only the gameplay tier runs {@link checkHideCover} first.
 */
export function hideSteps(state: EncounterState, actor: string): ResolvedStep[] {
  const c = state.combatants[actor];
  if (!c || hasStatus(c.statuses, HIDDEN_STATUS)) return [];
  return [{ op: "status_add", target: actor, status: HIDDEN_STATUS, expiry: { kind: "until_removed" } }];
}

/**
 * The shared **clear-Hidden** step builder every reveal trigger emits: if
 * `combatant` is Hidden, a `status_remove` of {@link HIDDEN_STATUS}; otherwise nothing
 * (idempotent — a non-Hidden unit reveals to zero steps, so the observer is a no-op). Keeps
 * the reveal a reified, invertible grounded step, never a direct mutation.
 */
export function clearHiddenSteps(combatant: Combatant | undefined): ResolvedStep[] {
  if (combatant && hasStatus(combatant.statuses, HIDDEN_STATUS)) {
    return [{ op: "status_remove", target: combatant.id, status: HIDDEN_STATUS }];
  }
  return [];
}

/**
 * The **shared Hidden-reveal sweep** — a full sweep over
 * every Hidden combatant that re-runs {@link checkHideCover} and emits {@link clearHiddenSteps}
 * for any unit the current board now exposes. This is the ONE sweep body two distinct trigger
 * points emit: the post-attack `coverDestroyedObserver` (`attackObservers.ts`, an attack/forced-
 * save resolution can destroy a Hidden unit's cover) and the post-move `hiddenRevealRecomputer`
 * (`spatialRecompute.ts`, a hostile's move can gain it a clean line the Hidden unit no longer has
 * cover from) — same predicate, same idempotent full-sweep shape, different firing point and
 * different `apply_resolved` kind label (a distinct seam earns its own audit trail). Extracted
 * here so both call sites share one body rather than drifting apart.
 *
 * Sorted by id (deterministic, replay-stable). Skips any non-Hidden combatant, and skips an
 * unplaced Hidden combatant BEFORE calling {@link checkHideCover} — `checkHideCover` hard-rejects
 * an unplaced hider ("is not placed on the map"), and a sweep that treated any non-`ok` verdict as
 * "exposed → reveal" would spuriously reveal an off-map unit that has no cover geometry to lose.
 * Still has cover from every hostile ⇒
 * stays Hidden (no step); now exposed ⇒ {@link clearHiddenSteps}. Idempotent — a sweep that
 * changes nothing emits zero steps (no broadcast).
 */
export function revealExposedHiddenSteps(state: EncounterState): ResolvedStep[] {
  const steps: ResolvedStep[] = [];
  // Sort ids so the emitted step order is deterministic (replay byte-identity), never dependent
  // on `combatants` insertion order — same discipline as `engagementRecomputer`.
  for (const id of Object.keys(state.combatants).sort()) {
    const c = state.combatants[id];
    if (c === undefined || !hasStatus(c.statuses, HIDDEN_STATUS)) continue;
    // Prowling (Infiltrator's "entering line of sight of hostiles or
    // moving from cover does not stop you from being Hidden"): a Prowling carrier is exempt from
    // the MID-TURN reveal sweep (this body runs from the post-move + cover-destroyed observers).
    // The carrier's OWN turn-end reveal check ({@link revealExposedAtTurnEndSteps})
    // reads `checkHideCover` directly, NOT this body, so an exposed infiltrator is still revealed
    // when its turn ENDS — exactly RAW's "during your turn" scope, no lingering permanent stealth.
    if (hasStatus(c.statuses, PROWLING_STATUS)) continue;
    // An unplaced hider has no cover geometry to lose — see the doc comment above.
    if (!c.pos) continue;
    // Still has cover from every hostile ⇒ stays Hidden. Now exposed ⇒ reveal.
    if (checkHideCover(state, id).ok) continue;
    steps.push(...clearHiddenSteps(c));
  }
  return steps;
}

/**
 * **The turn-end reveal**: RAW, a Hidden character stays Hidden only while it has cover or is
 * obscured, so at the end of its **own** turn this re-runs {@link checkHideCover} (the same check
 * the Hide action is gated by, so "exposed at turn end" means exactly "couldn't Hide here now") and
 * clears Hidden when it is in the open. A unit that isn't Hidden gets no steps. `turn_ended`'s
 * `reveal_exposed` rule (`referee/refereeCommand.ts`) runs it.
 *
 * **Also the Invisible→Hidden reveal** (RAW Core p.69: "If you're hiding while INVISIBLE, you lose
 * HIDDEN when you cease to be INVISIBLE unless you are in cover"). It runs after `turn_ended`'s
 * `status_expiry` rule, so a self-cast `source_next_turn_end` Invisible (Active Camouflage, the only
 * Invisible-granting content today) is already gone and `checkHideCover`'s Invisible short-circuit
 * no longer applies: exposed ⇒ reveal; in cover ⇒ stays Hidden. Running it before the expiry would
 * see Invisible still present and keep Hidden every time. `area_expiry` sits between the two for the
 * same reason: a unit Hidden in its own smoke is re-checked after the smoke disperses. Room-level
 * proof: `encounterRoom.test.ts`'s "Invisible→Hidden reveal at turn-end" test.
 *
 * Active Camouflage's "until you take damage" ends the Invisible as the damage lands
 * (`StatusInstance.endsOn`); after an attack, `attack_resolved`'s cover sweep re-checks the
 * now-visible unit, and a burn tick is followed by this check at the same turn end.
 *
 * **Accepted gaps** (all err toward stealth, never a false reveal): a GM's manual removal of
 * Invisible does not re-run this check; an ally-cast Invisible expiring at its source's turn end is
 * checked against the ended unit (the source), not the carrier. No content reaches the last one.
 */
export function revealExposedAtTurnEndSteps(state: EncounterState, ended: string): ResolvedStep[] {
  const c = state.combatants[ended];
  if (!c || !hasStatus(c.statuses, HIDDEN_STATUS)) return [];
  return checkHideCover(state, ended).ok ? [] : clearHiddenSteps(c);
}
